import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, RunTooSoonError } from '../../api/errors';
import { notificationsApi } from '../../api/notifications-api';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import { SearchableSelect } from '../../components/SearchableSelect';
import type { Customer, Site } from '../../domain/inventory';
import {
  deviceMessages,
  decisionLabel,
  deviceTypeLabel,
  emailStatusLabels,
  groupByRecipient,
  matchesDecision,
  messageHints,
  messageLabels,
  NO_OWNER,
  situationLabels,
  waitingLabel,
  type DeviceMessage,
  type DeviceNotice,
  type DecisionFilter,
} from '../../domain/notification';
import { siteLabel } from '../../lib/site-label';
import { SnoozeDialog } from './SnoozeDialog';

const decisionFilters: { value: DecisionFilter; label: string }[] = [
  { value: 'INCLUDED', label: 'Would be told' },
  { value: 'SKIPPED', label: 'Left out' },
  { value: 'NOT_DUE', label: 'Not due' },
  { value: 'ALL', label: 'All' },
];

function formatTime(iso: string | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** What a run sent, in words. */
function sentLabel(r: { watchOnly: boolean; emailsSent: number; emailsFailed: number }): string {
  if (r.watchOnly) return 'watch-only, nothing was sent';
  const sent = `${r.emailsSent} ${r.emailsSent === 1 ? 'email' : 'emails'} sent`;
  return r.emailsFailed > 0 ? `${sent}, ${r.emailsFailed} failed` : sent;
}

/**
 * The latest summary run (tidaltech/koi#8361), grouped by who it goes to,
 * with what each device's message is and why some are left out. A
 * summary email's Snooze link (?snooze=<item>) opens the snooze dialog.
 */
export function DeviceSummaryTab({
  sitesById,
  customersById,
  snoozeMaxDays,
}: {
  sitesById: ReadonlyMap<string, Site>;
  customersById: ReadonlyMap<string, Customer>;
  snoozeMaxDays: number;
}) {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<DecisionFilter>('INCLUDED');
  const [message, setMessage] = useState<'' | DeviceMessage>('');
  const [customerId, setCustomerId] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [snoozing, setSnoozing] = useState<Pick<DeviceNotice, 'itemId' | 'siteId'> | null>(null);
  const [params, setParams] = useSearchParams();
  const [confirmForce, setConfirmForce] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const latest = useQuery({
    queryKey: ['notifications', 'notices', 'latest'],
    queryFn: () => notificationsApi.listNotices(),
  });

  const run = useMutation({
    mutationFn: (force: boolean) => notificationsApi.runDeviceSummary(force),
    onSuccess: async () => {
      setConfirmForce(false);
      setRunError(null);
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      if (error instanceof RunTooSoonError) setConfirmForce(true);
      else setRunError(error instanceof ApiError ? error.message : 'The run failed. Try again.');
    },
  });

  const notices = useMemo(() => latest.data?.notices ?? [], [latest.data]);
  const runId = latest.data?.run?.id;
  const emails = useQuery({
    queryKey: ['notifications', 'emails', runId],
    queryFn: () => notificationsApi.listEmails(runId ?? ''),
    enabled: Boolean(runId) && latest.data?.run?.watchOnly === false,
  });

  const snoozeParam = params.get('snooze');
  useEffect(() => {
    if (!snoozeParam || !latest.isSuccess) return;
    const found = notices.find((n) => n.itemId === snoozeParam);
    setSnoozing(found ?? { itemId: snoozeParam });
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('snooze');
        return next;
      },
      { replace: true },
    );
  }, [snoozeParam, latest.isSuccess, notices, setParams]);
  const customerOptions = useMemo(
    () =>
      [...new Set(notices.map((n) => n.customerId ?? ''))]
        .filter(Boolean)
        .map((id) => {
          const c = customersById.get(id);
          return { value: id, label: c?.displayName ?? c?.name ?? id };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [customersById, notices],
  );
  const shown = notices.filter(
    (n) =>
      matchesDecision(n, decision) &&
      (!message || n.message === message) &&
      (!customerId || n.customerId === customerId) &&
      (!deviceType || n.deviceType === deviceType),
  );
  const groups = groupByRecipient(shown);
  const r = latest.data?.run;

  return (
    <section className="panel notification-tab" aria-label="Device summary">
      <div className="panel-heading">
        <div>
          <h2>Device summary</h2>
          {r ? (
            <p className="notification-subtitle">
              Run {formatTime(r.createTime)}
              {r.startedBy ? ` by ${r.startedBy}` : ''} · {r.devices} devices · {r.included} would
              be told · {r.skipped} left out · {r.notDue} not due
              {` · ${sentLabel(r)}`}
            </p>
          ) : null}
        </div>
        <div className="panel-heading-actions">
          <button
            type="button"
            className="primary"
            onClick={() => run.mutate(false)}
            disabled={!canMutate || run.isPending}
            title={canMutate ? undefined : 'Sign in to run the summary'}
          >
            {run.isPending ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      {confirmForce ? (
        <div className="warning-callout" role="alert">
          <p>
            The summary already ran recently{r ? `, on ${formatTime(r.createTime)}` : ''}. Running
            it again records a second run; nothing is sent either way.
          </p>
          <div className="dialog-actions">
            <button type="button" onClick={() => setConfirmForce(false)}>
              Keep that run
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => run.mutate(true)}
              disabled={run.isPending}
            >
              Run again anyway
            </button>
          </div>
        </div>
      ) : null}
      {runError ? (
        <p role="alert" className="form-error">
          {runError}
        </p>
      ) : null}

      <form
        className="customer-site-controls notification-filters"
        role="search"
        aria-label="Filter the summary"
        onSubmit={(event) => event.preventDefault()}
      >
        <label>
          <span>Show</span>
          <SearchableSelect
            ariaLabel="Show"
            value={decision}
            onChange={(value) => setDecision((value || 'INCLUDED') as DecisionFilter)}
            options={decisionFilters}
            placeholder="Would be told"
          />
        </label>
        <label>
          <span>Message</span>
          <SearchableSelect
            ariaLabel="Message"
            value={message}
            onChange={(value) => setMessage(value as '' | DeviceMessage)}
            options={[
              { value: '', label: 'Any message' },
              ...deviceMessages.map((m) => ({ value: m, label: messageLabels[m] })),
            ]}
            placeholder="Any message"
          />
        </label>
        <label>
          <span>Device</span>
          <SearchableSelect
            ariaLabel="Device"
            value={deviceType}
            onChange={setDeviceType}
            options={[
              { value: '', label: 'Any device' },
              ...[...new Set(notices.map((n) => n.deviceType ?? ''))]
                .filter(Boolean)
                .sort()
                .map((t) => ({ value: t, label: deviceTypeLabel(t) })),
            ]}
            placeholder="Any device"
          />
        </label>
        <label>
          <span>Customer</span>
          <SearchableSelect
            ariaLabel="Customer"
            value={customerId}
            onChange={setCustomerId}
            options={[{ value: '', label: 'Every customer' }, ...customerOptions]}
            placeholder="Every customer"
          />
        </label>
      </form>

      {latest.isLoading ? (
        <PageLoadingState label="Loading the summary" className="section-loading-state" />
      ) : null}
      {latest.isError ? (
        <ErrorState
          title="The summary could not be loaded"
          rpcName="ListDeviceNotices"
          onRetry={() => void latest.refetch()}
        />
      ) : null}
      {latest.isSuccess && !r ? (
        <p className="empty-note">No summary has run yet. Run it now to see this week’s.</p>
      ) : null}
      {latest.isSuccess && r && groups.length === 0 ? (
        r.included === 0 && decision === 'INCLUDED' && !message && !customerId && !deviceType ? (
          <div className="empty-note notification-nothing-new">
            <p>
              Nothing new to tell.{' '}
              {r.notDue > 0
                ? `${r.notDue} ${r.notDue === 1 ? 'device was' : 'devices were'} already flagged and ${r.notDue === 1 ? 'isn’t' : 'aren’t'} due a reminder yet`
                : 'No device needs a nudge'}
              {r.skipped > 0 ? ` · ${r.skipped} left out` : ''}.
            </p>
            <div className="notification-nothing-new-actions">
              {r.notDue > 0 ? (
                <button type="button" onClick={() => setDecision('NOT_DUE')}>
                  Show the {r.notDue} not due
                </button>
              ) : null}
              {r.skipped > 0 ? (
                <button type="button" onClick={() => setDecision('SKIPPED')}>
                  Show the {r.skipped} left out
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="empty-note">No devices match these filters.</p>
        )
      ) : null}

      {groups.map((group) => (
        <section
          key={group.recipient}
          className="notification-group"
          aria-label={`To ${group.recipient || 'nobody'}`}
        >
          <h3>
            {group.recipient === NO_OWNER
              ? 'Nobody to tell: no Customer Success owner set'
              : `To ${group.recipient}`}
            <span className="notification-muted">
              {' '}
              · {group.notices.length} {group.notices.length === 1 ? 'device' : 'devices'}
            </span>
          </h3>
          <div className="summary-table notification-table">
            <table>
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Device</th>
                  <th scope="col">Message</th>
                  <th scope="col">Waiting</th>
                  <th scope="col">Would go to</th>
                  <th scope="col">Decision</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {group.notices.map((n) => {
                  const site = n.siteId ? sitesById.get(n.siteId) : undefined;
                  return (
                    <tr key={n.id}>
                      <th scope="row" data-label="Site">
                        {site ? (
                          <Link
                            to={`/customers/${encodeURIComponent(site.customerId)}/sites/${encodeURIComponent(site.id)}`}
                          >
                            {siteLabel(site)}
                          </Link>
                        ) : (
                          (n.siteId ?? '—')
                        )}
                      </th>
                      <td data-label="Device">
                        <Link to={`/inventory/${encodeURIComponent(n.itemId)}`} className="mono">
                          {n.itemId}
                        </Link>
                        <small className="table-subtext">
                          {deviceTypeLabel(n.deviceType)}
                          {n.penId ? ` · Pen ${n.penId}` : ''}
                        </small>
                      </td>
                      <td data-label="Message">
                        {n.message ? <strong>{messageLabels[n.message]}</strong> : null}
                        <small className="table-subtext">
                          {[
                            n.situation ? situationLabels[n.situation] : '',
                            n.situation === 'REPLACED'
                              ? n.replacedByItemId
                                ? `By ${n.replacedByItemId}`
                                : ''
                              : n.message
                                ? messageHints[n.message]
                                : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </small>
                      </td>
                      <td data-label="Waiting">{waitingLabel(n)}</td>
                      <td data-label="Would go to">
                        {n.customerRecipients.length > 0 ? (
                          n.customerRecipients.join(', ')
                        ) : (
                          <span className="notification-muted">No site contact with an email</span>
                        )}
                        {n.language ? (
                          <small className="table-subtext">Language: {n.language}</small>
                        ) : null}
                      </td>
                      <td data-label="Decision">{decisionLabel(n)}</td>
                      <td className="notification-actions">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setSnoozing(n)}
                          disabled={!canMutate}
                        >
                          Snooze<span className="visually-hidden"> {n.itemId}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {emails.isSuccess && emails.data.length > 0 ? (
        <section className="notification-group" aria-label="Emails this run">
          <h3>
            Emails this run
            <span className="notification-muted"> · {emails.data.length}</span>
          </h3>
          <div className="summary-table notification-table">
            <table>
              <thead>
                <tr>
                  <th scope="col">To</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Devices</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {emails.data.map((e) => (
                  <tr key={e.id}>
                    <th scope="row" data-label="To">
                      {e.recipient}
                    </th>
                    <td data-label="Subject">{e.subject ?? ''}</td>
                    <td data-label="Devices">{e.noticeIds.length}</td>
                    <td data-label="Status">
                      {e.status ? emailStatusLabels[e.status] : ''}
                      {e.status === 'FAILED' && e.error ? (
                        <small className="table-subtext">{e.error}</small>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {snoozing ? (
        <SnoozeDialog
          itemId={snoozing.itemId}
          siteName={(() => {
            const site = snoozing.siteId ? sitesById.get(snoozing.siteId) : undefined;
            return site ? siteLabel(site) : 'the site';
          })()}
          maxDays={snoozeMaxDays}
          onClose={() => setSnoozing(null)}
        />
      ) : null}
    </section>
  );
}
